import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { createCompany } from "@/lib/companyApi";
import { ArrowRight, Building2, Loader2 } from "lucide-react";

export default function CompanyOnboarding() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    website: "",
    about: "",
    gstin: "",
    cin: "",
    domain: "",
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Company name is required");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await createCompany({
        ...form,
        website: form.website || undefined,
        about: form.about || undefined,
        gstin: form.gstin || undefined,
        cin: form.cin || undefined,
        domain: form.domain || undefined,
      });
      await refresh();
      navigate("/company", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create workspace");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-16">
      <div className="container-x max-w-3xl">
        <div className="inline-flex p-3 rounded-2xl bg-stone-900 text-amber-300 mb-6">
          <Building2 size={22} />
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight">
          Spin up your{" "}
          <span className="italic text-stone-500">recruiter workspace.</span>
        </h1>
        <p className="mt-4 text-stone-600 max-w-2xl">
          Takes 60 seconds. You can polish details later. We auto-issue your
          Wishire Verified badge — show it on every job post.
        </p>

        <form onSubmit={submit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field
            label="Company name *"
            testId="onboard-name"
            value={form.name}
            onChange={onChange("name")}
            placeholder="Lumen Studios Pvt. Ltd."
            full
          />
          <Field
            label="Website"
            testId="onboard-website"
            value={form.website}
            onChange={onChange("website")}
            placeholder="https://lumen.studio"
          />
          <Field
            label="Work-email domain"
            testId="onboard-domain"
            value={form.domain}
            onChange={onChange("domain")}
            placeholder="lumen.studio"
          />
          <Field
            label="GSTIN"
            testId="onboard-gstin"
            value={form.gstin}
            onChange={onChange("gstin")}
            placeholder="29AAACL1234D1Z5"
          />
          <Field
            label="CIN"
            testId="onboard-cin"
            value={form.cin}
            onChange={onChange("cin")}
            placeholder="U72200KA2014PTC074821"
          />
          <div className="md:col-span-2">
            <label className="overline">About your company</label>
            <textarea
              data-testid="onboard-about"
              value={form.about}
              onChange={onChange("about")}
              rows={3}
              className="mt-2 w-full px-4 py-3 rounded-2xl bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
              placeholder="A 12-person product studio shipping AI-native commerce tools…"
            />
          </div>
        </form>

        {error && (
          <p className="mt-4 text-red-700 text-sm" data-testid="onboard-error">
            {error}
          </p>
        )}

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="onboard-submit"
            className="btn-primary disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Creating workspace
              </>
            ) : (
              <>
                Create workspace <ArrowRight size={16} />
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate("/app")}
            className="text-sm text-stone-600 hover:text-stone-900"
          >
            Back to candidate workspace
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testId, value, onChange, placeholder, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="overline">{label}</label>
      <input
        type="text"
        data-testid={testId}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="mt-2 w-full px-4 py-3 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
      />
    </div>
  );
}
